import DatabaseModel from './DatabaseModel.js';
import CacheService from './Cache.js';
import { unwrapDto } from './responseUtils.js';
import { normalizarMotivoSalida } from '../utils/motivoSalidaPedido.js';
import { atarALaSesion } from '../utils/sesionDePeticiones.js';

const Pedido = Object.create(DatabaseModel);

// Las peticiones de este adaptador que no pueden pasar por `sendRequest` —porque su respuesta no
// tiene la forma de `ResponseDto`— van por `DatabaseModel.peticionDirecta`, y NO por
// `request.promise` a pelo. Ahí viven, en un solo sitio, las garantías de sesión que `sendRequest`
// da y que una llamada directa se pierde: el Authorization de la sesión vigente (sin él viajaría la
// cabecera global de `Framework7.request.setup`, que se queda con el token viejo cuando la sesión
// se renueva sin recargar la aplicación) y el tratamiento del token rechazado (un 401 cierra la
// sesión y lleva al login, en vez de devolver un error genérico que deja a la persona dando vueltas
// con una sesión que ya no vale).
//
// Las dos garantías salieron en la revisión del PR #1783 una ronda detrás de la otra —la 8 y la
// 10— por el mismo motivo: estaban cableadas en cada llamante. Si aparece una tercera, se añade en
// `peticionDirecta` y la tienen todas; lo que no se hace es volver a repetirla aquí petición por
// petición.
//
// La excepción es la promesa que estas funciones construyen alrededor de la petición (#663): solo
// la terminan su `then`/`catch`, y esos no se ejecutan si la sesión cambia mientras se espera. Por
// eso va atada con `atarALaSesion`: termina con la cancelación igual que la de dentro, y la
// pantalla que espera la comprobación previa al guardado no se queda colgada.

Pedido.model = 'pedido';
Pedido.modelFront = 'pedidos';

Pedido.getPlanificar = function(params) {
    var self = this;
    return atarALaSesion(new Promise(function(resolve, reject) {
        self.peticionDirecta({
            url: self.server_movilidad + self.prefix + self.model + '/getlineasplanificar/',
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            crossDomain: true,
            data: params.data
        }).then(function({ data, xhr, status }) {
            if (data.error) {
                reject(data);
            } else {
                resolve(data.list);
            }
        }).catch(function({ xhr, status, message }) {
            try {
                var obj = JSON.parse(xhr.response);
                reject(obj.message ? obj.message : obj.error);
            } catch (e) {
                reject(xhr.response);
            }
        });
    }));
};

Pedido.getLineaCreateExpManual = function(params) {
    var self = this;
    return atarALaSesion(new Promise(function(resolve, reject) {
        self.peticionDirecta({
            url: self.server_movilidad + self.prefix + self.model + '/getLineaCreateExpManual/',
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            crossDomain: true,
            data: params.data
        }).then(function({ data, xhr, status }) {
            if (data.error) {
                reject(data);
            } else {
                resolve(data.dto);
            }
        }).catch(function({ xhr, status, message }) {
            try {
                var obj = JSON.parse(xhr.response);
                reject(obj.message ? obj.message : obj.error);
            } catch (e) {
                reject(xhr.response);
            }
        });
    }));
};

/** #425 · Lo que se dice cuando la consulta no ha contestado y no hay nada mejor que decir. */
const CONSULTA_ABIERTAS_FALLIDA = 'No se han podido consultar las planificaciones abiertas del'
    + ' pedido. Comprueba la conexión y vuelve a intentarlo.';

/**
 * #425 · El rechazo de `planificacionesAbiertas`, SIEMPRE con un `message` que se pueda enseñar.
 *
 * Es lo que impide que un fallo de la consulta llegue vacío a quien espera. `xhr.response` es `""`
 * cuando la petición no llega a contestar —desconexión, corte del proxy, respuesta sin cuerpo—, y
 * un rechazo falsy se cuela por cualquier `if (error)`: en el borrado dejaba la pantalla muda
 * después de que la persona hubiera confirmado, y en el guardado abría un diálogo en blanco
 * (revisión CODEX del PR #1783, ronda 6). La garantía vive aquí, en el único sitio por el que pasan
 * los dos caminos, y no repartida por cada `catch`.
 *
 * Lleva también el `status` de la respuesta: un `catch` que se queda solo con el texto no deja
 * distinguir después «el servidor ha dicho que no» de «no ha contestado» (ronda 10).
 */
function rechazoAbiertas(origen, status) {
    let mensaje = origen;
    if (typeof mensaje === 'string' && mensaje.trim()) {
        try {
            mensaje = JSON.parse(mensaje);
        } catch (e) {
            // No era JSON: el texto crudo ya es lo mejor que hay.
        }
    }
    // `message` antes que `error`: en el cuerpo de error de Spring, `error` es el nombre genérico
    // del estado («Internal Server Error») y `message` lo que de verdad explica algo. Es el mismo
    // orden que usa `Pedido.getPlanificar`.
    if (mensaje && typeof mensaje === 'object') {
        mensaje = mensaje.message || mensaje.error;
    }
    if (typeof mensaje !== 'string' || !mensaje.trim()) {
        mensaje = CONSULTA_ABIERTAS_FALLIDA;
    }
    return status === undefined ? { message: mensaje } : { message: mensaje, status: status };
}

/**
 * #425 · Las planificaciones del pedido que seguirían sumando OEB si se cancelara o se desactivara:
 * las PLANIFICADAS y las REALIZADAS cuya línea aún no tiene TPC. Solo lectura.
 *
 * Devuelve siempre un array (vacío si no hay ninguna), para que quien lo llame no tenga que
 * distinguir «no hay» de «no ha contestado»: lo segundo llega por el `reject`, y nunca en vacío
 * (ver `rechazoAbiertas`).
 */
Pedido.planificacionesAbiertas = function(params) {
    const self = this;
    return atarALaSesion(new Promise(function(resolve, reject) {
        if (!params || !params.itemId) {
            // Sin id no hay nada que consultar. Es un fallo de programación, pero el rechazo lleva
            // mensaje igual: lo que no puede pasar es que la pantalla se quede muda.
            reject({ error: 'itemId not defined', message: CONSULTA_ABIERTAS_FALLIDA });
            return;
        }
        self.peticionDirecta({
            url: self.server_movilidad + self.prefix + self.model + '/' + params.itemId + '/planificaciones-abiertas',
            method: 'GET',
            contentType: 'application/json',
            dataType: 'json',
            crossDomain: true
        }).then(function({ data }) {
            if (data.error) {
                reject(rechazoAbiertas(data.error));
            } else {
                resolve(data.list || []);
            }
        }).catch(function({ xhr, status }) {
            reject(rechazoAbiertas(xhr ? xhr.response : null, status));
        });
    }));
};

/**
 * Borrado del pedido. `params.confirmacion` ({confirmado, motivo}) es la confirmación de la #425
 * cuando el pedido tiene planificaciones abiertas; va en la URL porque un DELETE no lleva cuerpo.
 * Sin ella se comporta exactamente como antes.
 *
 * Por eso el motivo pasa por `normalizarMotivoSalida`: aquí es parte de la URL, y una URL que se
 * pasa de largo la corta el navegador, el proxy o el servidor con un 414, o sea que el pedido no
 * se borra y el aviso de error no habla del motivo (revisión CODEX del PR #1783). Lo que acota es
 * el tamaño del texto ya codificado, no su cuenta de caracteres, y deja siempre caracteres
 * completos: `encodeURIComponent` lanza con media pareja de un par sustituto suelta.
 */
Pedido.delete = function(params) {
    var self = this;
    let sufijoConfirmacion = '';
    if (params && params.confirmacion && params.confirmacion.confirmado) {
        sufijoConfirmacion = '?confirmado=true&motivo='
            + encodeURIComponent(normalizarMotivoSalida(params.confirmacion.motivo));
    }
    return atarALaSesion(new Promise(function(resolve, reject) {
        if (!params.itemId) reject({ error: 'itemId not defined' });
        self.peticionDirecta({
            url: self.server_movilidad + self.prefix + self.model + '/' + params.itemId + sufijoConfirmacion,
            method: 'DELETE',
            contentType: 'application/json',
            dataType: 'json',
            crossDomain: true,
            data: params.data
        }).then(function({ data, xhr, status }) {
            if (data.error) {
                reject(data);
            } else {
                resolve(data.dto);
            }
        }).catch(function({ xhr, status, message }) {
            try {
                reject(JSON.parse(xhr.response).error);
            } catch (e) {
                reject(xhr.response);
            }
        });
    }));
}

Pedido.enviarBC = function (params) {
    var self = this;
    return atarALaSesion(new Promise(function (resolve, reject) {
        if (!params.itemId) reject({ error: 'itemId not defined' });
        self.peticionDirecta({
            url: self.server_movilidad + self.prefix + self.model + '/enviarBC/' + params.itemId,
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            crossDomain: true,
            data: params.data
        }).then(function ({ data, xhr, status }) {
            if (data.error) {
                reject(data);
            } else {
                resolve(data.dto);
            }
        }).catch(function ({ xhr, status, message }) {
            try {
                reject(JSON.parse(xhr.response).error);
            } catch (e) {
                reject(xhr.response);
            }
        });
    }));
}

Pedido.crearSegundas = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/crearsegundas',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};
Pedido.desvincularNuevaPrimera = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/desvincularnuevaprimera',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};
Pedido.vincularNuevaPrimera = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/vincularnuevaprimera',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};
Pedido.vincularLineas = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/vincularlineas',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};
Pedido.desvincularLineas = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/desvincularlineas',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};
Pedido.crearNuevaPrimera = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/crearnuevaprimera',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};
Pedido.desplanificar = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/desplanificar',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};
Pedido.borrarLineas = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/borrarlineas',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};
Pedido.borrarSegundas = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/borrarsegundas',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};
/**
 * Comprueba el reparto de EJECUCIÓN de unas líneas antes de guardarlas (#424).
 *
 * Entrada: `{ idPedido, lineas: [{ numero, repEjecucion: [...] }] }` — el reparto tal como está en
 * la pantalla, todavía sin guardar.
 *
 * Salida: `{ lineas: [{ numeroLinea, avisos: [{ codigo, mensaje, propuestaUnion }] }] }`. Solo
 * vienen las líneas con algún aviso, así que `lineas: []` significa «se guarda sin preguntar».
 * `propuestaUnion` solo viene en PERSONA_DUPLICADA y es el reparto completo que quedaría al unir
 * las filas repetidas: se aplica SOLO si la persona lo acepta.
 *
 * No guarda nada y no bloquea nada: los tres avisos dejan continuar con un motivo.
 */
Pedido.validarRepartoEjecucion = function(params) {
    const self = this;
    const peticion = {
        url: self.server_movilidad + self.prefix + self.model + '/validarrepartoejecucion',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(peticion, false).then(function(res) {
        return unwrapDto(res) || { lineas: [] };
    });
};

Pedido.borrarNuevaPrimera = function(params) {
    var self = this;
    var params = {
        url: self.server_movilidad + self.prefix + self.model + '/borrarNuevaPrimera',
        method: 'POST',
        data: params.data
    };
    return self.sendRequest(params, false).then(function(res) {
        CacheService.clearCache(self.model);
        return res;
    });
};

export default Pedido;